/**
 * Marca del despliegue en el navegador.
 *
 * `public/index.html` pide `api/v1/default/branding/` antes de cargar el
 * bundle y deja el resultado en `window.AV_BRANDING` (null si falla). Aquí
 * se normaliza sobre la marca neutra (nunca lanza: lo que no es válido se
 * ignora y queda el valor neutro) y se aplica: título, favicon y variables CSS
 * `--av-*` que consume el SCSS con el valor del tema como respaldo.
 *
 * Este módulo no importa nada de la aplicación (`constants/defaultValues`
 * lo usa al arrancar y un import circular rompería el orden de carga).
 */
import { NEUTRAL_BRANDING, LANGUAGES, TERM_KEYS } from './neutral';

const BOOTSTRAP_KEY = 'AV_BRANDING';
const MAX_VALUE_LENGTH = 700;
const FORBIDDEN_CSS = /[;{}\\<>]|url\(/i;

const isText = (value) => typeof value === 'string' && value.trim() !== '';
const isSafeCss = (value) =>
  isText(value) &&
  value.length <= MAX_VALUE_LENGTH &&
  !FORBIDDEN_CSS.test(value);
const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

// Claves que el backend puede mandar a null a propósito ("sin valor"), p. ej.
// una marca sin imagen de fondo: ese null manda sobre la marca neutra.
const NULLABLE = ['gradient', 'image', 'border_color'];

const mergeGroup = (base, raw, accepts) => {
  const result = { ...base };
  if (!isPlainObject(raw)) return result;
  Object.keys(base).forEach((key) => {
    const value = raw[key];
    if (value === null && NULLABLE.includes(key)) {
      result[key] = null;
    } else if (accepts(key, value)) {
      result[key] = value;
    }
  });
  return result;
};

const normalizeTerms = (raw) => {
  const terms = {};
  if (!isPlainObject(raw)) return terms;
  TERM_KEYS.forEach((key) => {
    const value = raw[key];
    if (isText(value)) {
      terms[key] = value;
    } else if (isPlainObject(value)) {
      const perLanguage = {};
      LANGUAGES.forEach((lang) => {
        if (isText(value[lang])) perLanguage[lang] = value[lang];
      });
      if (Object.keys(perLanguage).length) terms[key] = perLanguage;
    }
  });
  return terms;
};

/** Devuelve una marca completa a partir de lo que dijo el backend (o nada). */
export const normalizeBranding = (raw) => {
  const base = NEUTRAL_BRANDING;
  if (!isPlainObject(raw)) return { ...base, logos: { ...base.logos } };
  const textOrKeep = (_key, value) => isSafeCss(value);
  return {
    version: base.version,
    name: isText(raw.name) ? raw.name : base.name,
    title: isText(raw.title) ? raw.title : base.title,
    favicon: isSafeCss(raw.favicon) ? raw.favicon : base.favicon,
    logos: mergeGroup(base.logos, raw.logos, textOrKeep),
    colors: mergeGroup(base.colors, raw.colors, textOrKeep),
    background: mergeGroup(base.background, raw.background, textOrKeep),
    card: mergeGroup(base.card, raw.card, textOrKeep),
    font: isSafeCss(raw.font) ? raw.font : base.font,
    default_language: LANGUAGES.includes(raw.default_language)
      ? raw.default_language
      : base.default_language,
    terms: normalizeTerms(raw.terms),
    features: {
      ...base.features,
      ...(isPlainObject(raw.features) &&
      typeof raw.features.sso_login === 'boolean'
        ? { sso_login: raw.features.sso_login }
        : {}),
    },
  };
};

let cached = null;

/** Marca vigente de esta carga de página (la de `window.AV_BRANDING` o la neutra). */
export const getBranding = () => {
  if (!cached) {
    let raw = null;
    try {
      raw = typeof window !== 'undefined' ? window[BOOTSTRAP_KEY] : null;
    } catch (error) {
      raw = null;
    }
    cached = normalizeBranding(raw);
  }
  return cached;
};

/** Si el despliegue ofrece una opción (p. ej. `sso_login`); por defecto sí. */
export const isFeatureEnabled = (name) =>
  getBranding().features[name] !== false;

/**
 * Si el login ofrece el enlace de SSO: el entorno lo habilita en el build
 * (`REACT_APP_SSO_ENABLED=true`, mismo criterio que la base de Noname) y la
 * marca no lo apaga (`features.sso_login`).
 */
export const isSsoLoginAvailable = () =>
  process.env.REACT_APP_SSO_ENABLED === 'true' && isFeatureEnabled('sso_login');

/** Solo para tests. */
export const resetBranding = () => {
  cached = null;
};

/** Origen del backend, igual que `setAxiosConfig` de la base. */
export const backendOrigin = (env = process.env) => {
  const { REACT_APP_PROTOCOL, REACT_APP_BACK_IP, REACT_APP_BACK_PORT } = env;
  if (!REACT_APP_BACK_IP) return '';
  const port = REACT_APP_BACK_PORT ? `:${REACT_APP_BACK_PORT}` : '';
  return `${REACT_APP_PROTOCOL}://${REACT_APP_BACK_IP}${port}`;
};

/** Las rutas `/api/...` son del backend; el resto (`/assets/...`, https) se dejan como están. */
export const resolveAssetUrl = (path, origin = backendOrigin()) =>
  typeof path === 'string' && path.startsWith('/api/')
    ? `${origin}${path}`
    : path;

const cssUrl = (path) =>
  `url("${resolveAssetUrl(path).replace(/["\\\n]/g, '')}")`;

/** Idioma por defecto de esta instalación: el de la marca o, si no hay, el del navegador. */
export const brandedDefaultLocale = (fallback) =>
  getBranding().default_language || fallback;

/** Borde de tarjeta como atajo CSS, o null si la marca no define uno visible. */
const cardBorder = (card) =>
  card.border_color && card.border_width && card.border_width !== '0'
    ? `${card.border_width} solid ${card.border_color}`
    : null;

/**
 * Variables CSS que publica la marca. Un valor nulo no genera variable: el SCSS
 * usa entonces el valor del tema. Con el tema oscuro solo se publican logos y
 * tipografía: los tokens de color están pensados para el tema claro.
 */
export const brandingCssVars = (branding, { isDark = false } = {}) => {
  const vars = {};
  const set = (name, value) => {
    if (value !== null && value !== undefined && value !== '') {
      vars[`--av-${name}`] = value;
    }
  };

  ['login', 'sidebar', 'mobile'].forEach((key) =>
    set(`logo-${key}`, branding.logos[key] ? cssUrl(branding.logos[key]) : null)
  );
  set('font', branding.font);
  if (isDark) return vars;

  const { colors, background, card } = branding;
  set('primary', colors.primary);
  set('foreground', colors.foreground);
  set('text', colors.text);
  set('page-text', colors.page_text);
  [1, 2, 3, 4, 5, 6].forEach((n) =>
    set(`theme-${n}`, colors[`theme_color_${n}`])
  );
  set('bg-color', background.color);
  set('bg-gradient', background.gradient);
  // El oscurecimiento del fondo es para fotos; con un degradado de marca se quita.
  if (background.gradient) {
    set('bg-filter', 'none');
    set('logo-filter', 'none');
  }
  // Sin imagen de marca: con degradado, ninguna; sin él, la del tema.
  if (background.image) {
    set('bg-image', cssUrl(background.image));
  } else if (background.gradient) {
    set('bg-image', 'none');
  }
  set('card-bg', card.bg);
  set('card-radius', card.radius);
  set('card-blur', card.blur);
  set('card-border', cardBorder(card));
  return vars;
};

const setFavicon = (doc, href) => {
  let link = doc.querySelector('link[rel~="icon"]');
  if (!link) {
    link = doc.createElement('link');
    link.rel = 'icon';
    doc.head.appendChild(link);
  }
  link.removeAttribute('type');
  link.href = href;
};

const isDarkTheme = (storageKey) => {
  try {
    return (window.localStorage.getItem(storageKey) || '').includes('dark');
  } catch (error) {
    return false;
  }
};

/** Aplica la marca al documento. Nunca lanza: un fallo deja la interfaz con el tema. */
export const applyBranding = (
  branding = getBranding(),
  {
    doc = typeof document !== 'undefined' ? document : null,
    themeStorageKey,
  } = {}
) => {
  if (!doc) return;
  try {
    // eslint-disable-next-line no-param-reassign
    doc.title = branding.title;
    if (branding.favicon) setFavicon(doc, resolveAssetUrl(branding.favicon));
    const vars = brandingCssVars(branding, {
      isDark: isDarkTheme(themeStorageKey),
    });
    Object.keys(vars).forEach((name) =>
      doc.documentElement.style.setProperty(name, vars[name])
    );
    // El SCSS solo pinta borde de tarjeta si existe esta clase (ver %blur-card).
    doc.documentElement.classList.toggle(
      'av-card-border',
      '--av-card-border' in vars
    );
  } catch (error) {
    // La marca es presentación: si algo falla se queda lo que haya.
    console.error('No se pudo aplicar la marca', error);
  }
};
